import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import styles from './WalletLedger.module.css';
import Header from '../../../Header';
import { local_signage, local_url } from '../../../Const';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export const WalletLedger = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 200;
    const [sortConfig, setSortConfig] = useState({ key: 'transTime', direction: 'desc' });

    // Financial Year
    const [finYears, setFinYears] = useState([]);
    const [selectedFinYear, setSelectedFinYear] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(dayjs());

    // Fetch financial years on mount
    useEffect(() => {
        const authToken = localStorage.getItem('AuthorizationToken');
        fetch(`${local_signage}/api/v1/fetchfinancialyear`, {
            headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
        })
            .then(r => r.json())
            .then(years => {
                const active = (years || []).filter(y => y.Deleted === 'N');
                setFinYears(active);
                // Auto-select the FY that includes today
                const today = new Date();
                const current = active.find(y => {
                    const from = new Date(y.FromDate);
                    const to = new Date(y.ToDate);
                    return today >= from && today <= to;
                }) || active[0];
                if (current) applyFinYear(current, active);
            })
            .catch(console.error);
    }, []);

    const applyFinYear = (fy, list) => {
        const fyData = list ? list.find(y => y.FinYearId === fy.FinYearId) : fy;
        if (!fyData) return;
        setSelectedFinYear(fyData.FinYearId);
        setFromDate(dayjs(fyData.FromDate));
        setToDate(dayjs(fyData.ToDate));
    };

    const handleFinYearChange = (e) => {
        const id = e.target.value;
        const fy = finYears.find(y => y.FinYearId === id);
        if (fy) applyFinYear(fy, null);
    };

    const fyMin = selectedFinYear ? dayjs((finYears.find(y => y.FinYearId === selectedFinYear)?.FromDate || '')) : null;
    const fyMax = selectedFinYear ? dayjs((finYears.find(y => y.FinYearId === selectedFinYear)?.ToDate || '')) : null;

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem("AuthorizationToken");
            const headers = {
                'Authorization': `Bearer ${authToken}` || authToken,
                'Content-Type': 'application/json'
            };

            const formattedFrom = fromDate ? fromDate.format('YYYYMMDD') : '';
            const formattedTo = toDate ? toDate.format('YYYYMMDD') : '';

            const [res, walletRes] = await Promise.all([
                fetch(`${local_url}/api/Subscription/ReportSubscriptionLedgerSummary/${selectedFinYear || 'ALL'}/${formattedTo}`, { method: 'GET', headers }),
                fetch(`${local_signage}/api/v1/reportwallettransactions?Mobile=ALL&FilterbyChannel=ALL&FromDate=${formattedFrom}&ToDate=${formattedTo}`, { method: 'GET', headers }).catch(() => null)
            ]);

            if (!res.ok) throw new Error(`API error! status: ${res.status}`);

            const result = await res.json();
            const rows = result.Data || [];

            // Build lookup from wallet transactions: mobile → { CustomerName, CustId }
            const walletLookup = new Map();
            if (walletRes && walletRes.ok) {
                const walletResult = await walletRes.json();
                const walletRows = Array.isArray(walletResult) ? walletResult : (walletResult.data || []);
                walletRows.forEach(w => {
                    const mob = (w.Mobile || '').toString().trim();
                    if (mob) walletLookup.set(mob, { CustomerName: w.CustomerName || '', CustId: w.CustId || '' });
                });
            }

            setData(rows.map(item => {
                const mobile = (item.mobileNumber || '').toString().trim() || '-';
                const walletInfo = walletLookup.get(mobile) || {};
                return {
                    CustomerName: item.CustomerName || walletInfo.CustomerName || '-',
                    Mobile: mobile,
                    transTime: item.transTime ? item.transTime.split('T')[0] : '-',
                    AreaName: item.SFAreaName || '-',
                    POSCustId: item.POSCustId || walletInfo.CustId || '-',
                    RefCustomerId: item.RefCustomerId || '-',
                    opening: parseFloat(item.opening) || 0,
                    POScredit: parseFloat(item.POScredit) || 0,
                    Webcredit: parseFloat(item.Webcredit) || 0,
                    POSdebit: parseFloat(item.POSdebit) || 0,
                    Webdebit: parseFloat(item.Webdebit) || 0,
                    totalCredit: parseFloat(item.totalCredit) || 0,
                    totalDebit: parseFloat(item.totalDebit) || 0,
                    balance: parseFloat(item.balance) || 0,
                };
            }));

        } catch (err) {
            setError(err.message);
            console.error("Error fetching wallet ledger:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedFinYear) {
            fetchData();
        }
    }, [selectedFinYear]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = React.useMemo(() => {
        const filtered = data.filter(item => {
            const searchRegex = new RegExp(searchTerm, 'i');
            return (
                searchRegex.test(item.CustomerName || '') ||
                searchRegex.test(item.Mobile || '') ||
                searchRegex.test(item.POSCustId || '') ||
                searchRegex.test(item.RefCustomerId || '')
            );
        });

        return [...filtered].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            
            if (aVal === bVal) return 0;
            if (!aVal) return 1;
            if (!bVal) return -1;
            
            let comparison = 0;
            if (sortConfig.key === 'transTime') {
                comparison = new Date(aVal) - new Date(bVal);
            } else if (typeof aVal === 'number' && typeof bVal === 'number') {
                comparison = aVal - bVal;
            } else {
                comparison = aVal.toString().localeCompare(bVal.toString());
            }
            
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [data, searchTerm, sortConfig]);

    const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
    const pagedData = sortedData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const handleSearch = (val) => {
        setSearchTerm(val);
        setCurrentPage(1);
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '₹0.00';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === '-') return '-';
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = String(d.getFullYear()).slice(-2);
        return `${day}-${month}-${year}`;
    };

    const exportToXLSX = () => {
        if (!sortedData.length) return;
        const exportData = sortedData.map((row) => ({
            'Customer Name': row.CustomerName,
            'Mobile Number': row.Mobile,
            'Last Trans Time': row.transTime,
            'Area Name': row.AreaName,
            'POS Cust ID': row.POSCustId,
            'Ref Customer ID': row.RefCustomerId,
            'Opening Balance': row.opening,
            'POS Credit': row.POScredit,
            'Web Credit': row.Webcredit,
            'POS Debit': row.POSdebit,
            'Web Debit': row.Webdebit,
            'Total Credit': row.totalCredit,
            'Total Debit': row.totalDebit,
            'Balance': row.balance,
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Wallet Ledger Summary');
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        XLSX.writeFile(workbook, `wallet_ledger_${timestamp}.xlsx`);
    };

    return (
        <>
            <Header />
            <div className={styles.wallet_dashboard}>
                <div className={styles.wallet_filters_section}>
                    <div className={styles.wallet_filters_grid} style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', alignItems: 'center' }}>
                        {/* Financial Year Dropdown */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>Financial Year</label>
                            <select
                                value={selectedFinYear || ''}
                                onChange={handleFinYearChange}
                                style={{
                                    height: '38px', padding: '0 10px', border: '1px solid #ddd',
                                    borderRadius: '6px', fontSize: '14px', background: '#fff',
                                    cursor: 'pointer', minWidth: '140px'
                                }}
                            >
                                {finYears.map(fy => (
                                    <option key={fy.FinYearId} value={fy.FinYearId}>
                                        FY {fy.FinYearId.slice(0, 2)}-{fy.FinYearId.slice(2)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* Date Range with MUI DatePicker */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>From Date</label>
                                    <DatePicker
                                        value={fromDate}
                                        onChange={(newVal) => setFromDate(newVal)}
                                        format="DD-MM-YYYY"
                                        readOnly={!!selectedFinYear}
                                        minDate={fyMin}
                                        maxDate={fyMax}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { 
                                                    '& .MuiInputBase-root': { height: '38px', backgroundColor: selectedFinYear ? '#f8f9fa' : '#fff' },
                                                    width: '150px'
                                                }
                                            }
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>To Date</label>
                                    <DatePicker
                                        value={toDate}
                                        onChange={(newVal) => setToDate(newVal)}
                                        format="DD-MM-YYYY"
                                        minDate={fyMin}
                                        maxDate={fyMax}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: { 
                                                    '& .MuiInputBase-root': { height: '38px', backgroundColor: '#fff' },
                                                    width: '150px'
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </LocalizationProvider>
                        <div className={styles.wallet_search_group} style={{ flex: '1', minWidth: '220px' }}>
                            <input
                                type="text"
                                className={styles.wallet_search_input}
                                placeholder="Search by name, mobile, POS ID..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                            <button className={`${styles.wallet_btn} ${styles.wallet_btn_success}`} onClick={fetchData} disabled={loading} style={{ height: '38px', margin: '0' }}>
                                {loading ? 'Loading...' : 'Fetch'}
                            </button>
                        </div>
                    </div>
                </div>
                <div className={styles.wallet_results_summary}>
                    <span>Showing {sortedData.length} records (Page {currentPage} of {totalPages || 1})</span>
                    <button
                        type="button"
                        className={`${styles.wallet_btn} ${styles.wallet_btn_success}`}
                        onClick={exportToXLSX}
                        disabled={loading || sortedData.length === 0}
                    >
                        Export XLSX
                    </button>
                </div>
                <div className={styles.wallet_table_section}>
                    {loading && (
                        <div className={styles.wallet_loader_container}>
                            <div className={styles.wallet_loader}></div>
                            <p>Loading ledger data...</p>
                        </div>
                    )}
                    {!loading && error && (
                        <div className={styles.wallet_no_data}>
                            <p>Error: {error}</p>
                        </div>
                    )}
                    {!loading && !error && sortedData.length === 0 && (
                        <div className={styles.wallet_no_data}>
                            <p>No transactions found.</p>
                        </div>
                    )}
                    {!loading && !error && sortedData.length > 0 && (
                        <div className={styles.wallet_table_container}>
                            <table className={styles.wallet_data_table}>
                                <thead>
                                    <tr>
                                        <th>Customer Name</th>
                                        <th>Mobile</th>
                                        <th 
                                            onClick={() => handleSort('transTime')} 
                                            style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                                            title="Click to sort by Last Trans Time"
                                        >
                                            Last Trans Time {sortConfig.key === 'transTime' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th>POS Cust ID</th>
                                        <th>Opening Balance</th>
                                        <th>POS Credit</th>
                                        <th>Web Credit</th>
                                        <th>POS Debit</th>
                                        <th>Web Debit</th>
                                        <th>Total Credit</th>
                                        <th>Total Debit</th>
                                        <th>Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedData.map((row, index) => (
                                        <tr key={index}>
                                            <td
                                                className={styles.wallet_customer_name}
                                                style={{ cursor: 'pointer', color: '#1e88e5', textDecoration: 'underline' }}
                                                onClick={() => navigate(`/WalletLedgerDetail/${row.Mobile}/${encodeURIComponent(row.CustomerName || 'UNKNOWN')}?from=${fromDate}&to=${toDate}&fy=${selectedFinYear || ''}`)}
                                            >
                                                {row.CustomerName}
                                            </td>
                                            <td>{row.Mobile}</td>
                                            <td>{formatDate(row.transTime)}</td>
                                            <td className={styles.wallet_ticket_id}>{row.POSCustId}</td>
                                            <td className={styles.wallet_amount} style={{ color: '#1e88e5' }}>{formatCurrency(row.opening)}</td>
                                            <td className={styles.wallet_amount} style={{ color: '#28a745' }}>{formatCurrency(row.POScredit)}</td>
                                            <td className={styles.wallet_amount} style={{ color: '#28a745' }}>{formatCurrency(row.Webcredit)}</td>
                                            <td className={styles.wallet_amount} style={{ color: '#dc3545' }}>{formatCurrency(row.POSdebit)}</td>
                                            <td className={styles.wallet_amount} style={{ color: '#dc3545' }}>{formatCurrency(row.Webdebit)}</td>
                                            <td className={styles.wallet_amount} style={{ color: '#28a745', fontWeight: 600 }}>{formatCurrency(row.totalCredit)}</td>
                                            <td className={styles.wallet_amount} style={{ color: '#dc3545', fontWeight: 600 }}>{formatCurrency(row.totalDebit)}</td>
                                            <td className={styles.wallet_amount} style={{ color: row.balance < 0 ? '#dc3545' : '#1e88e5', fontWeight: 600 }}>
                                                {formatCurrency(row.balance)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {!loading && !error && totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px 0', borderTop: '1px solid #e0e0e0', background: '#fff' }}>
                            <button className={`${styles.wallet_btn} ${styles.wallet_btn_success}`} onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={{ padding: '4px 10px' }}>«</button>
                            <button className={`${styles.wallet_btn} ${styles.wallet_btn_success}`} onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} style={{ padding: '4px 10px' }}>‹</button>
                            <span style={{ fontSize: '14px' }}>Page {currentPage} of {totalPages}</span>
                            <button className={`${styles.wallet_btn} ${styles.wallet_btn_success}`} onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} style={{ padding: '4px 10px' }}>›</button>
                            <button className={`${styles.wallet_btn} ${styles.wallet_btn_success}`} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} style={{ padding: '4px 10px' }}>»</button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default WalletLedger;
